import { create } from "zustand";
import type { MasterDataState } from "../types/masterData";

/**
 * @owner shared (dominio transversal — excepción documentada D2)
 *
 * Fuente de verdad (SoT) para datos maestros: países, géneros, tipos de documento.
 * React Query se usa solo para fetch (ver useMasterData.ts).
 * Los consumidores leen de este store, NO de useQuery selectors.
 * Decisión D2: NO eliminar este store ni migrar a RQ puro.
 */
export const useMasterDataStore = create<MasterDataState>()((set, get) => ({
  // Estado inicial
  countries: [],
  genders: [],
  documentTypes: [],
  isLoading: false,
  isLoaded: false,
  error: null,

  // Actions básicas
  setCountries: (countries) => set({ countries }),
  setGenders: (genders) => set({ genders }),
  setDocumentTypes: (documentTypes) => set({ documentTypes }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setLoaded: (isLoaded) => set({ isLoaded }),
      
      reset: () => set({
        countries: [],
        genders: [],
        documentTypes: [],
        isLoading: false,
        isLoaded: false,
        error: null,
      }),

      // Helpers para buscar por ID
      getCountryById: (id: number | null) => {
        const { countries } = get();
        return countries.find(country => country.id === id);
      },

      getGenderById: (id: number | null) => {
        const { genders } = get();
        return genders.find(gender => gender.id === id);
      },

      getDocumentTypeById: (id: number | null) => {
        const { documentTypes } = get();
        return documentTypes.find(docType => docType.id === id);
      },

      // Helpers para buscar por código/letra
      getGenderByLetter: (letter: string) => {
        const { genders } = get();
        return genders.find(gender => 
          gender.letter.toLowerCase() === letter.toLowerCase()
        );
      },

      getDocumentTypeByInitials: (initials: string) => {
        const { documentTypes } = get();
        return documentTypes.find(docType =>
          docType.identification_initials.toLowerCase() === initials.toLowerCase()
        );
      },
    })
  );

// Selectores específicos para facilitar el uso
export const useCountries = () => useMasterDataStore(state => state.countries);
export const useGenders = () => useMasterDataStore(state => state.genders);
export const useDocumentTypes = () => useMasterDataStore(state => state.documentTypes);
export const useMasterDataLoading = () => useMasterDataStore(state => state.isLoading);
export const useMasterDataLoaded = () => useMasterDataStore(state => state.isLoaded);
export const useMasterDataError = () => useMasterDataStore(state => state.error);

// Selectores para helpers
export const useMasterDataHelpers = () => {
  const store = useMasterDataStore.getState();
  return {
    getCountryById: store.getCountryById,
    getGenderById: store.getGenderById,
    getDocumentTypeById: store.getDocumentTypeById,
    getGenderByLetter: store.getGenderByLetter,
    getDocumentTypeByInitials: store.getDocumentTypeByInitials,
  };
};
